import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { CircularProgress, IconButton } from '@mui/material';
import { Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { useSelector } from 'react-redux';
import AgentPerformanceMetrics from '../Agentperformancemetrics';
import { useAuthToken } from '../../TokenContext';
import axios from 'axios';
import PodActivity from '../PodActivity';
import Podactivitytalent from '../Podactivitytalent';
import KanbanBoard from '../KanbanBoard';
import KanbanboardTalent from '../KanbanboardTalent';
import Podretromanager from '../Podretromanager';
import PodactivitytalentRetro from '../PodactivitytalentRetro';
import './ArtemyStyles.css';

const POD_META = {
  'pod-ops': { label: 'OPERATIONAL', name: 'Astra Operational', color: 'var(--artemy-accent)', desc: 'Manages standups, ticket triage, and operational handoffs' },
  'pod-product': { label: 'PRODUCT', name: 'Astra Product', color: 'var(--artemy-green)', desc: 'PRD analysis, intent drift detection, and roadmap alignment' },
  'pod-eng': { label: 'ENGINEERING', name: 'Astra Engineering', color: 'var(--artemy-yellow)', desc: 'Code review, PR analysis, architecture alerts, and CI/CD monitoring' },
  'pod-mgmt': { label: 'MANAGEMENT', name: 'Astra Executive', color: 'var(--artemy-purple)', desc: 'Executive briefs, what-if simulations, and escalation management' },
};

function StatusPill({ status }) {
  const map = { active: 'var(--artemy-green)', provisioning: 'var(--artemy-yellow)', stopped: 'var(--artemy-text-dim)', error: 'var(--artemy-red)' };
  const color = map[status] || 'var(--artemy-text-dim)';
  return <span style={{ fontFamily: 'var(--artemy-font-mono)', fontSize: 10, color, background: `${color}1a`, padding: '2px 8px', borderRadius: 4, border: `1px solid ${color}33`, textTransform: 'uppercase' }}>{status}</span>;
}

export default function PodDetail({ podId }) {
  const history = useHistory();
  const token = useAuthToken();
  const { user } = useSelector((s) => s.auth);
  const meta = POD_META[podId] || POD_META['pod-ops'];
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('roster');

  useEffect(() => {
    if (!token || !process.env.REACT_APP_CLIENT_LISTAGENTHANDLER) { setLoading(false); return; }
    axios.post(process.env.REACT_APP_CLIENT_LISTAGENTHANDLER, { manager_id: user?.data?.[0]?.emp_id }, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => {
        const data = res.data?.data;
        setAgents(Array.isArray(data) ? data : []);
      })
      .catch(() => setAgents([]))
      .finally(() => setLoading(false));
  }, [token, user, podId]);

  const activeCount = Array.isArray(agents) ? agents.filter((a) => a.status === 'active').length : 0;

  return (
    <div className="artemy-fade-up">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 22 }}>
        <div>
          <div style={{ fontFamily: 'var(--artemy-font-mono)', fontSize: 10, letterSpacing: '1.2px', textTransform: 'uppercase', color: meta.color, marginBottom: 5 }}>{meta.label} POD CLUSTER</div>
          <div className="artemy-page-title">{meta.name}</div>
          <div className="artemy-page-sub">{meta.desc}</div>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button className="artemy-btn artemy-btn-ghost" onClick={() => history.push('/control-plane/pods')}>
            ← All Pods
          </button>
          <button className="artemy-btn artemy-btn-primary" onClick={() => history.push('/AgentsList')}>
            Configure
          </button>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 14, marginBottom: 16 }}>
        {[
          { label: 'Active Agents', value: activeCount, color: meta.color },
          { label: 'Actions Today', value: 284, color: 'var(--artemy-text-primary)' },
          { label: 'Confidence', value: '95%', color: 'var(--artemy-green)' },
          { label: 'Autonomy Level', value: 'L1', color: 'var(--artemy-text-primary)' },
        ].map((m) => (
          <div key={m.label} className="artemy-card" style={{ padding: 18 }}>
            <div style={{ fontFamily: 'var(--artemy-font-mono)', fontSize: 11, color: 'var(--artemy-text-secondary)', marginBottom: 8 }}>{m.label}</div>
            <div style={{ fontFamily: 'var(--artemy-font-display)', fontSize: 26, fontWeight: 700, color: m.color }}>{m.value}</div>
          </div>
        ))}
      </div>

      <div style={{ display: 'flex', gap: 24, borderBottom: '1px solid var(--artemy-border)', marginBottom: 20 }}>
        {[
          { id: 'roster', label: 'Agents Roster' },
          { id: 'approvals', label: 'Pending Approvals' },
          { id: 'metrics', label: 'Agent Metrics' },
          { id: 'alerts', label: 'Agent Alerts' },
        ].map(t => (
          <div
            key={t.id}
            onClick={() => setActiveTab(t.id)}
            style={{
              paddingBottom: 12,
              cursor: 'pointer',
              fontFamily: 'var(--artemy-font-mono)', fontSize: 11, letterSpacing: '.5px', textTransform: 'uppercase',
              color: activeTab === t.id ? 'var(--artemy-text-primary)' : 'var(--artemy-text-secondary)',
              borderBottom: activeTab === t.id ? `2px solid ${meta.color}` : '2px solid transparent',
              transition: 'all 0.2s',
              fontWeight: activeTab === t.id ? 600 : 400
            }}
          >
            {t.label}
          </div>
        ))}
      </div>

      {activeTab === 'roster' && (
        <div className="artemy-card" style={{ overflow: 'hidden' }}>
          <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--artemy-border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ fontFamily: 'var(--artemy-font-display)', fontSize: 14, fontWeight: 600 }}>
              Agent Roster <span style={{ fontFamily: 'var(--artemy-font-mono)', fontSize: 10.5, color: 'var(--artemy-text-dim)', background: 'var(--artemy-bg-base)', padding: '2px 8px', borderRadius: 4, marginLeft: 8, border: '1px solid var(--artemy-border)' }}>{(agents || []).length} agents discovered</span>
            </div>
          </div>
          {loading ? (
            <div style={{ padding: 40, display: 'flex', justifyContent: 'center' }}><CircularProgress size={28} sx={{ color: meta.color }} /></div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr>
                  {['Agent Identity', 'Module Type', 'Status', 'Last Verification', 'Trigger Mechanism', 'Autonomy Strategy', 'Actions'].map((h) => (
                    <th key={h} style={{ textAlign: 'left', padding: '11px 16px', fontFamily: 'var(--artemy-font-mono)', fontSize: 10, letterSpacing: '.8px', color: 'var(--artemy-text-dim)', textTransform: 'uppercase', borderBottom: '1px solid var(--artemy-border)', fontWeight: 400 }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {!Array.isArray(agents) || agents.length === 0 ? (
                  <tr><td colSpan={7} style={{ padding: 40, textAlign: 'center', color: 'var(--artemy-text-dim)' }}>No agents yet — deploy a controller to initiate session</td></tr>
                ) : (
                  agents.map((a, i) => (
                    <tr key={a.id || i} style={{ borderBottom: '1px solid rgba(255,255,255,.03)', transition: 'background .15s' }}>
                      <td style={{ padding: '12px 16px', fontWeight: 600, color: 'var(--artemy-text-primary)' }}>{a.name}</td>
                      <td style={{ padding: '12px 16px', color: 'var(--artemy-text-secondary)', fontSize: 12 }}>{a.agent_type}</td>
                      <td style={{ padding: '12px 16px' }}><StatusPill status={a.status} /></td>
                      <td style={{ padding: '12px 16px', fontFamily: 'var(--artemy-font-mono)', fontSize: 11, color: 'var(--artemy-text-dim)' }}>
                        {a.last_run_at ? new Date(a.last_run_at).toLocaleString() : '—'}
                      </td>
                      <td style={{ padding: '12px 16px', color: 'var(--artemy-text-secondary)', fontSize: 12 }}>{a.trigger_type}</td>
                      <td style={{ padding: '12px 16px' }}>
                        <span style={{ fontFamily: 'var(--artemy-font-mono)', fontSize: 10, color: 'var(--artemy-accent)', background: 'rgba(61,127,255,.1)', padding: '2px 8px', borderRadius: 4, border: '1px solid rgba(61,127,255,.2)', textTransform: 'uppercase' }}>L1 Recommend</span>
                      </td>
                      <td style={{ padding: '12px 16px' }}>
                        <IconButton size="small" onClick={() => history.push('/AgentsList')}><EditIcon fontSize="small" /></IconButton>
                        <IconButton size="small" onClick={() => history.push('/AgentsList')}><DeleteIcon fontSize="small" /></IconButton>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          )}
        </div>
      )}

      {activeTab === 'approvals' && (
        <div className="artemy-card" style={{ padding: 20, minHeight: 400, background: 'var(--artemy-bg-surface)' }}>
           <div style={{ fontFamily: 'var(--artemy-font-display)', fontSize: 16, fontWeight: 600, marginBottom: 16 }}>Pending Approvals</div>
           <div style={{ color: 'var(--artemy-text-dim)' }}>No pending approvals at this time.</div>
        </div>
      )}

      {activeTab === 'metrics' && (
        <div className="artemy-card" style={{ padding: 20, minHeight: 400, background: 'var(--artemy-bg-surface)' }}>
           <AgentPerformanceMetrics />
        </div>
      )}

      {activeTab === 'alerts' && (
        <div className="artemy-card" style={{ padding: 20, minHeight: 400, background: 'var(--artemy-bg-surface)' }}>
           <div style={{ fontFamily: 'var(--artemy-font-display)', fontSize: 16, fontWeight: 600, marginBottom: 16 }}>Agent Alerts</div>
           <div style={{ color: 'var(--artemy-text-dim)' }}>No active alerts for this pod.</div>
        </div>
      )}

      <div style={{ height: 40 }} />
    </div>
  );
}
